import { z } from 'zod';

/**
 * A bare version of the shared ZodIssue properties, without methods.
 */
export interface ValidationError {
    code: string;
    path: Array<string | number>;
    message: string;
}

export interface ValidationFailure {
    errors: ValidationError[];
}

export interface MalformedResponse {
    systemErrors: z.ZodError;

    /**
     * Escape hatch to be used in production where we cross our fingers and pray if we get
     * a bad response from the server
     */
    recovery: any;
}

export const isMalformedResponse = (
    response: ValidationFailure | MalformedResponse
): response is MalformedResponse => {
    return 'systemErrors' in response;
};

export type ApiErrorReason = 'Unavailable' | 'Unauthorized' | 'Unknown' | 'Empty';

interface ApiError {
    reason: ApiErrorReason;
    message: string;
}

export type NetworkError = ApiError | Error;

export class MalformedResponseError extends Error implements MalformedResponse {
    path: string;
    formatted: string;
    systemErrors: z.ZodError;
    recovery: any;

    constructor(path: string, malformedResponse: MalformedResponse) {
        const formatted = JSON.stringify(malformedResponse.systemErrors.flatten().fieldErrors);
        super(`API response validation failure from ${path} : ${formatted} `);
        this.systemErrors = malformedResponse.systemErrors;
        this.recovery = malformedResponse.recovery;
        this.path = path;
        this.formatted = formatted;
    }
}

export type ApiClientError = MalformedResponseError | NetworkError | ValidationFailure;

export const isMalformedResponseError = (error: unknown): error is MalformedResponseError => {
    return error instanceof MalformedResponseError;
};

export const isValidationFailure = (error: ApiClientError): error is ValidationFailure => {
    if (isMalformedResponseError(error)) {
        return false;
    }

    if (error instanceof Error) {
        return false;
    }

    if ('reason' in error) {
        return false;
    }

    return true;
};

export const isNetworkError = (error: ApiClientError) => {
    if (isMalformedResponseError(error)) {
        return false;
    }

    if (isValidationFailure(error)) {
        return false;
    }
    return true;
};
